import { ExecuteActionRequest, GameState, ReadingResult, StartReadingRequest, StopReadingRequest, SwitchRoomGameState, PcState, PlaneTicketRequest, KeyState, WeldingState, KeyUsed, SlapState, AchievementUnlocked, Achievement, Inventory, InventoryState, GameOver, ActionReference, GameObjectAlias, GameObjectReference, GameAndActionReference, endEventSoundRequest } from "@shared/types";
import { BaseRouteService } from "./BaseRouteService";

/**
 * Service to communicate with game routes of the server application
 */
export class GameRouteService extends BaseRouteService {
    /**
     * Execute room entrance for the current player on the server application
     *
     * @param roomAlias Alias of the room to go to
     *
     * @returns A type of game state, or `undefined` if no answer has been received
     */
    public async executeRoomNavigation(
        roomAlias: string
    ): Promise<GameState | undefined> {
        try {
            return await this.postJsonApi<GameState, SwitchRoomGameState>("game/navigation", {
                type: "switch-room",
                roomAlias: roomAlias,
            });
        }
        catch {
            return undefined;
        }
    }

    /**
     * Get the images for the current player on the server application, after read action has been clicked
     *
     * @param actionAlias The alias of the object that the player has stopped reading
     *
     * @param objectAliases New text to display
     *
     * @returns New images, text to display and object alias, or `undefined` if no answer has been received
     */
    public async getReadingPages(
        actionAlias: string,
        objectAliases: string[]
    ): Promise<ReadingResult | undefined> {
        try {
            return await this.postJsonApi<ReadingResult, StartReadingRequest>("game/reading", {
                type: "start-reading",
                action: actionAlias,
                objects: objectAliases,
            });
        }
        catch {
            return undefined;
        }
    }

    // public async eatItem(object: GameObjectReference): Promise<void> {
    //     return this.getJsonApi("game/eat-item", object);
    // }

    /**
     * Execute the change of game state for the current player on the server application, after the player has stopped reading
     *
     * @param object The alias of the object that the player has stopped reading
     *
     * @param text New text to display
     *
     * @returns A type of game state, or `undefined` if no answer has been received
     */
    public async executeStopReading(
        object: string,
        text: string[]
    ): Promise<GameState | undefined> {
        try {
            return await this.postJsonApi<GameState, StopReadingRequest>("game/reading", {
                type: "stop-reading",
                object: object,
                text: text,
            });
        }
        catch {
            return undefined;
        }
    }

    public async endEventSound(
        name: string
    ): Promise<GameState | undefined> {
        try {
            return await this.postJsonApi<GameState, endEventSoundRequest >("game/end-event-sound", {
                name: name,
            });
        }
        catch {
            return undefined;
        }
    }

    public async exitGame(): Promise<GameOver> {
        return this.getJsonApi<GameOver>("game/exit-game");
    }

    public async restartGame(): Promise<GameOver> {
        return this.getJsonApi<GameOver>("game/restart-game");
    }

    public async getDeathMessage(): Promise<number> {
        return this.getJsonApi<number>("game/death-message");
    }

    public async loadCheckpoint(lastCheckpoint: string): Promise<void> {
        return this.getJsonApi("game/load-checkpoint", {
            roomAlias: lastCheckpoint,
        });
    }

    public async savePlayerSession(): Promise<void> {
        return this.getJsonApi("game/save-playersession");
    }

    public async killedBySeaBeast(): Promise<void> {
        return this.getJsonApi("game/seabeast-death");
    }

    public async seaBeastDefeated(): Promise<void> {
        return this.getJsonApi("game/seabeast-defeated");
    }

    public async getFightingSeaBeastState(): Promise<boolean> {
        return this.postJsonApi<boolean>("game/seabeast-state");
    }

    public async seaBeastDodged(): Promise<void> {
        return this.getJsonApi("game/seabeast-dodged");
    }

    public async seaBeastFailed(): Promise<void> {
        return this.getJsonApi("game/seabeast-failed");
    }

    public async clearCutscene(): Promise<void> {
        return this.getJsonApi("game/clear-cutscene");
    }

    public async afterPlaneCrash(): Promise<void> {
        return this.getJsonApi("game/after-crash");
    }

    public async getCrashAnnouncementState(): Promise<boolean> {
        return this.getJsonApi<boolean>("game/crash-announcement-state");
    }

    public async crashAnnouncementPlayed(): Promise<void> {
        return this.getJsonApi("game/crash-announcement-played");
    }

    /**
     * Get a type of game state for the current player from the server application
     *
     * @returns A type of game state for the current player
     */
    public async getGameState(): Promise<GameState> {
        return this.getJsonApi<GameState>("game/state");
    }

    public async getEndOfGameState(): Promise<boolean> {
        return this.getJsonApi<boolean>("game/end-game-state");
    }

    public async setEndOfGame(): Promise<void> {
        return this.getJsonApi("game/set-end-game");
    }

    public async getCutsceneNumber(): Promise<number> {
        return this.getJsonApi<number>("game/cutscene-number");
    }

    public async getInventoryState(): Promise<InventoryState> {
        return this.getJsonApi<InventoryState>("game/inventory-state");
    }

    public async getInventory(): Promise<Inventory[]> {
        return this.getJsonApi<Inventory[]>("game/inventory");
    }

    public async openInventory(): Promise<void> {
        return this.getJsonApi("game/open-inventory");
    }

    public async closeInventory(): Promise<void> {
        return this.getJsonApi("game/close-inventory");
    }

    public async getAchievementNumber(): Promise<number> {
        return this.getJsonApi<number>("game/achievement-number");
    }

    public async closeAchievement(achievementNumber: number): Promise<void> {
        return this.postJsonApi("game/close-achievement", {
            case: achievementNumber,
        });
    }

    public async getAchievementUnlocked(): Promise<AchievementUnlocked[]> {
        return this.getJsonApi<AchievementUnlocked[]>("game/achievement-unlocked");
    }

    public async unlockAchievement(achievementCase: number): Promise<Achievement> {
        return this.getJsonApi<Achievement>("game/unlock-achievement", {
            case: achievementCase,
        });
    }

    /**
     * Get the PC state for the current player from the server application
     *
     * @returns The PC state for the current player
     */
    public async getPcState(): Promise<PcState> {
        return this.getJsonApi<PcState>("game/pc-state");
    }

    public async keyState(): Promise<KeyState> {
        return this.getJsonApi<KeyState>("game/key-state");
    }

    public async keyTurned(): Promise<KeyUsed> {
        return this.getJsonApi<KeyUsed>("game/key-turned");
    }

    /**
     * Turn the PC off for the current player on the server application
     */
    public async turnPcOff(): Promise<void> {
        return this.getJsonApi("game/turn-pc-off");
    }

    /**
     * Buy a plane ticket for the current player on the server application
     *
     * @returns The plane ticket request
     */
    public async buyPlaneTicket(): Promise<PlaneTicketRequest> {
        return this.getJsonApi<PlaneTicketRequest>("game/buy-plane-ticket");
    }

    public async getWeldingState(): Promise<WeldingState> {
        return this.getJsonApi<WeldingState>("game/welding-state");
    }

    public async weldingCompleted(): Promise<void> {
        return this.getJsonApi("game/welding-complete");
    }

    public async resetCutsceneNumber(): Promise<void> {
        return this.getJsonApi("game/reset-cutscene");
    }

    public async slapped(): Promise<void> {
        return this.getJsonApi("game/slapped");
    }

    public async getSlapState(): Promise<SlapState> {
        return this.getJsonApi("game/being-slapped");
    }

    public async getActionsForObject(
        objectAlias: string
    ): Promise<ActionReference[]> {
        return await this.postJsonApi<ActionReference[], GameObjectAlias>("game/actions-for-object", {
            gameObjectAlias: objectAlias,
        });
    }

    public async gameObjectForAction(
        objectReference: GameObjectReference,
        actionReference: ActionReference
    ): Promise<boolean> {
        return await this.postJsonApi<boolean, GameAndActionReference>("game/object-and-action", {
            gameObjectReference: objectReference,
            actionReference: actionReference,
        });
    }

    /**
     * Execute an action for the current player on the server application
     *
     * @param actionAlias Alias of the action to execute
     * @param objectAliases Aliases of the game objects to execute the action on
     *
     * @returns A type of game state, or `undefined` if the action was unhandled. This can indicate the action requires a second game object to work.
     */
    public async executeAction(
        actionAlias: string,
        objectAliases?: string[]
    ): Promise<GameState | undefined> {
        try {
            return await this.postJsonApi<GameState, ExecuteActionRequest>("game/action", {
                action: actionAlias,
                objects: objectAliases,
            });
        }
        catch {
            return undefined;
        }
    }
}
